// src/api/aiKeyPointsApi.js
import axios from 'axios';

const KEY_POINTS_API_URL = '/api/key-points';

/**
 * Extract various types of information from text using AI
 * @param {string} text - The text to analyze
 * @param {number} maxPoints - Maximum number of points to extract for each type
 * @param {string[]} extractionTypes - Types of extractions to perform
 * @returns {Promise<Object>} - The extracted information
 */
export const getKeyPointsFromAI = async (text, maxPoints = 5, extractionTypes = ["keyPoints", "detailedInsights", "quotes"]) => {
  try {
    console.log("Calling Key Points API with text length:", text.length);
    
    const response = await axios.post(
      KEY_POINTS_API_URL,
      { 
        text,
        maxPoints,
        extractionTypes: extractionTypes.slice(0, 3) // Limit to 3 types max
      },
      {
        withCredentials: true, // Sends authentication cookies automatically
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log("Key Points API response received with types:", Object.keys(response.data));
    
    // Ensure we have all the expected extraction types
    const result = {};
    
    extractionTypes.forEach(type => {
      result[type] = response.data[type] || [];
    });
    
    // Always include keyPoints for backward compatibility
    if (!result.keyPoints) {
      result.keyPoints = [];
    }
    
    // Add token usage if available
    if (response.data.token_usage) {
      result.token_usage = response.data.token_usage;
    }
    
    return result;
    } catch (error) {
        console.error("Error extracting information:", error.message);

        if (error.response?.status === 402) {
          // Throw the exact HTML message from backend
          const backendMessage = error.response?.data?.detail;
          throw new Error(backendMessage || "Insufficient token balance");
        }

        if (error.response?.status === 401) {
          throw new Error("Authentication failed");
        }

        if (error.response?.data?.detail) {
          throw new Error(error.response.data.detail);
        }

        throw new Error("AI extraction failed");
      }
};

/**
 * Modify existing AI results with a user prompt
 * @param {Object} existingResults - Current AI extraction results
 * @param {string} modificationPrompt - User's prompt to modify results
 * @param {string[]} extractionTypes - Original extraction types
 * @returns {Promise<Object>} - The modified results
 */
export const modifyAIResults = async (existingResults, modificationPrompt, extractionTypes) => {
  try {
    console.log("Calling Modify Results API with prompt:", modificationPrompt);
    
    const response = await axios.post(
      '/api/modify-results',
      { 
        existingResults,
        prompt: modificationPrompt,
        extractionTypes
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log("Modify Results API response received");
    return response.data;
  } catch (error) {
    console.error("Error modifying results:", error.message);
    throw error;
  }
};